// FILE: src/config/exams.js
// Single source of truth for per-certification exam configuration.
// All question counts and durations live here — never hardcoded in components.
//
// ── Targeted Test (user-configured) ─────────────────────────────────────────
//
// Distinct from the post-results `targetedQuiz` (which is auto-launched from
// the Results page based on weak phases). The Targeted Test mode lets the
// learner pick metadata filters and a question count up-front from the
// ModeSelector tiles. See TargetedTestConfig.js.
//
//   targetedTest: {
//     allowedCounts:        number[]   // selectable question counts
//     defaultCount:         number     // pre-selected count
//     secondsPerQuestion:   number     // timer = ceil(count * spq / 60) min
//   }
//
// All exams share the same defaults for now; per-exam overrides are easy to
// add by setting `targetedTest` inside the exam block.
//
// ── Category distribution (Full Exam only) ───────────────────────────────────
//
// `categoryDistribution` defines the target percentage of questions per
// category for full-length exam simulations.  These mirror the official
// blueprint published by each certification body.
//
// Percentages must sum to 100.  At session-creation time the values are
// converted to absolute question counts (rounded, then adjusted so the total
// equals fullExam.totalQuestions exactly).
//
// If the question bank has fewer questions in a category than required, the
// shortfall is filled by drawing extra questions from the categories that
// have the most surplus.
//
// `fullExamDistribution` (optional) overrides WHICH question field the Full
// Exam stratifies on. A cert whose published exam is scored on a dimension
// other than `category` declares it here, e.g. CPMAI:
//
//   fullExamDistribution: { field: 'ecoDomain', percentages: { ... } }
//
// `categoryDistribution` stays in place for that cert and still describes the
// methodology/teaching mix; it is simply not the Full Exam stratifier. Only
// ONE dimension can be enforced per draw — a 120-question sim cannot hit two
// complete taxonomies exactly — so the other taxonomy floats.
//
// ── Experimental questions (Phase 2, migration 012) ──────────────────────────
//
// Each mode (fullExam / practiceExam / targetedQuiz) may carry an optional
// `experimental` config:
//
//   experimental: {
//     enabled: boolean,   // master switch — when false the mode behaves
//                         // exactly as before Phase 2 shipped
//     count:   number,    // how many experimental questions to mix into a
//                         // session of this mode.  Subtracted from the total
//                         // (NOT added on top), so a 20-question practice
//                         // with `count: 3` shows 17 scored + 3 experimental.
//   }
//
// Phase 2 ships with experimental DISABLED for both PMP and CPMAI on every
// mode.  Bryan flips the per-exam flags when ready to start mixing
// calibration questions into live sessions.
//
// Graceful degradation: if the experimental pool is too small to satisfy
// `count`, the shortfall is backfilled with scored questions so total
// session length never changes (see questionService.applyExperimentalSelection).

import { isFeatureEnabled } from './featureFlags';
import { PMI_PMOCP_FOCUS_AREAS, FOCUS_AREA_UNCLASSIFIED, CPMAI_ECO_DOMAINS } from './examCategories';

const exams = {
  pmp: {
    examId: 'pmp',
    // Case Study Question Framework (PR 3). `enabled` turns the learner
    // entry on for this cert once the global caseStudiesEnabled flag is on;
    // `label` is the learner-facing name (internal model stays case_studies).
    caseStudies: { enabled: true, label: 'Case Study Practice', summary: '10–12 questions · 35–45 min' },
    displayName: 'PMP®',
    fullDescription: 'Project Management Professional',
    // Per-exam display overrides for Targeted Test metadata.  Filter VALUES
    // sent to questionService still use the underlying raw category value
    // (e.g. 'Execution') — these only change what the learner SEES.
    metadataDisplay: {
      category: {
        groupLabel: 'Phases',
        // Only the five PMI process groups should appear under "Phases".
        // This filters out non-phase categories that may exist in the
        // bank (e.g. knowledge areas), and dedupes against any spurious
        // 'Executing' value by allowlisting only the canonical 'Execution'.
        valueAllowlist: [
          'Initiating',
          'Planning',
          'Execution',
          'Monitoring and Controlling',
          'Closing',
        ],
        valueLabels: { 'Execution': 'Executing' },
      },
    },
    fullExam: {
      // Case Studies inside Full Sims (Phase 2, migration 076). Active only
      // when caseStudiesInExamsEnabled is on AND published cases exist.
      // placement 'frontloaded': cases occupy Q1..K, Break 1 seals them (D2).
      //
      // ⚠️ `count`, `minQuestions` and `maxQuestions` are a PM PIXEL DESIGN
      // ASSUMPTION. They have NO PMI source. The 2026 PMP ECO describes a
      // "case-study section" and a "Case or Scenario" question type, and places
      // the first break after that section — but it publishes no number of
      // cases, no questions per case, and no section size. Verified against the
      // July 2026 ECO on 2026-09-06.
      //
      // These values therefore express how faithful a rehearsal we want to
      // offer, not a fact about the real exam. Change them only for a stated
      // DESIGN reason and record that reason here — never to make the current
      // case inventory eligible. As of 2026-09-07 only 2 of 6 published PMP
      // cases fall inside 8–10 (the others are 12, 12, 6 and 4), so a draw
      // yields K = 20 rather than the ~30 this config asks for. That is content
      // inventory debt, not a simulator defect: the fix is authoring another
      // high-quality 8–10-question case, not widening the window.
      //
      // NOTE the size window is applied CLIENT-SIDE in
      // services/examCaseBlocks.js selectExamCases(). The get_exam_case_blocks
      // RPC does NOT filter on question count — it returns every published case
      // for the certification. (docs/case-exam-integration-plan.md §A1 describes
      // a server-side filter that does not exist.)
      caseSection: { count: 3, minQuestions: 8, maxQuestions: 10, placement: 'frontloaded' },
      totalQuestions: 180,
      durationMinutes: 240, // PMI raised 230→240 with the July 2026 exam update
      experimental: { enabled: false, count: 0 },
    },
    // ── Scheduled exam breaks (Simulation / Full Exam only) ──────────────────
    //
    // WHAT PMI PUBLISHES (PMP ECO, July 2026, "PMP Certification Exam
    // Information", verified 2026-09-06):
    //   "there are two 10-minute breaks in the exam. The first break occurs
    //    after the case-study section, and the second break occurs
    //    approximately midway through the independent question portion"
    //   "once you review your responses and start your break you will not be
    //    able to return to the questions from the previous section"
    // PMI publishes the COUNT and the DURATION. It does NOT publish a question
    // number for either break, and has not done so since the January 2021 ECO
    // (which specified Q60/Q120 for the pre-July-2026 exam).
    //
    // WHERE THE REAL PLACEMENT COMES FROM: services/examCaseBlocks.js
    // computeSessionBreaks() derives Break 1 from the ACTUAL case-section
    // boundary and spreads the remaining break(s) through the independent
    // portion. That is the 2026 topology and it is what a learner gets whenever
    // a case section can be assembled.
    //
    // ⚠️ THE ARRAY BELOW IS THE NO-CASE / LEGACY FALLBACK ONLY. It applies when
    // case studies are disabled by flag, when no published case exists, or when
    // no case meets fullExam.caseSection's size window. Only `durationMinutes`
    // here is PMI-sourced; the positions are DERIVED by getExamBreaks() as even
    // fractions of fullExam.totalQuestions and are NOT PMI-published values.
    // Never present a fallback position as the real exam's break point.
    //
    // Reaching a break locks all prior questions (still viewable; answers and
    // flags can no longer be changed) whether the learner takes or skips it.
    // The exam clock is paused for the duration of the break.
    // Config-driven: components read this via getExamBreaks() — break
    // thresholds are never hardcoded anywhere else.
    breaks: [
      { durationMinutes: 10 },
      { durationMinutes: 10 },
    ],
    practiceExam: {
      defaultQuestions: 20,
      durationMinutes: 30,
      experimental: { enabled: false, count: 0 },
    },
    targetedQuiz: {
      defaultQuestions: 15,
      durationMinutes: 20,
      experimental: { enabled: false, count: 0 },
    },
    // Exam Ready threshold for Performance History / Readiness charts.
    // Locked at 75 to match the readiness model "exam_ready" bucket
    // (PLAN-phase-a-design.md §4). Phase D adds a learner override.
    pass_target_pct: 75,
    // Study cadence used by the Phase D rule engine (studyPlanService).
    // Weekly pattern: <days_on> study days, then <review_days> review,
    // then <rest_days> rest. Tune per cert here without touching code.
    study_cadence: {
      days_on:     5,
      review_days: 1,
      rest_days:   1,
    },
    // Target number of full simulations to complete before sitting the exam.
    // Drives a hard rule in the rule engine: reach this count by the final
    // week. Phase D default is 3 across all certs.
    target_full_sims: 3,
    categoryDistribution: {
      'Initiating':                 13,
      'Planning':                   24,
      'Execution':                  31,
      'Monitoring and Controlling': 25,
      'Closing':                     7,
    },
    // ── CTA badges ───────────────────────────────────────────────────────────
    // Home-page badges (PMBOK 8 Ready, platform/feature, telemetry) are no
    // longer configured per-exam here. They live in the canonical badge system:
    //   • src/config/badges.js     — the registry (BADGES) + colours + rules
    //   • src/services/badgeService.js — selection / rotation / telemetry logic
    //   • src/components/BadgeCallout.js + PlatformBadgeRow.js — rendering
    // To badge a cert, add/activate an entry in badges.js with
    // `certificationTargets: ['<examId>']` — no change to this file or a
    // component is required.
  },
  cpmai: {
    examId: 'cpmai',
    displayName: 'CPMAI™',
    fullDescription: 'Certified Professional in Managing AI',
    fullExam: {
      totalQuestions: 120,
      // PMI-CPMAI Examination Content Outline (September 2025), "PMI-CPMAI
      // Exam Information": 120 total questions (100 scored + 20 pretest) and
      // "Total Time Allotted 160 minutes". The tutorial and survey are
      // explicitly NOT counted against testing time, and the exam has no
      // scheduled breaks, so 160 is clean answering time and is directly
      // comparable to this field's semantics (see the `breaks` note below).
      // Source: https://www.pmi.org/-/media/pmi/documents/public/pdf/certifications/pmicpmai-exam-content-outline2025-updated.pdf
      // Verified 2026-09-06. Was 90 — a value carried over from the original
      // 77-question CPMAI configuration and never updated for the PMI-CPMAI
      // exam; it under-ran the real exam by 70 minutes (45s/q vs 80s/q).
      durationMinutes: 160,
      experimental: { enabled: false, count: 0 },
    },
    // CPMAI has no scheduled breaks — the real exam runs straight through.
    // ECO, Exam Information: "For the PMI-CPMAI exam, there are no scheduled
    // breaks." Verified 2026-09-06.
    breaks: [],
    // ── Targeted Test display (PMI ECO domains) ─────────────────────────────
    // `ecoDomain` is a GATED metadata field: questionService.extractMetadataOptions
    // surfaces it ONLY for certs that declare a valueAllowlist here. PMI-ACP also
    // populates questions.eco_domain (its own, drifting vocabulary) and MUST NOT
    // get this block — its ECO values stay out of the learner UI on purpose.
    // Filter VALUES sent to questionService remain the canonical DB strings;
    // valueLabels only change what the learner sees.
    metadataDisplay: {
      ecoDomain: {
        groupLabel: 'PMI Exam Domains',
        valueAllowlist: CPMAI_ECO_DOMAINS,
        valueLabels: {
          'Support Responsible and Trustworthy AI Efforts': 'Responsible & Trustworthy AI',
          'Identify Business Needs and Solutions':          'Business Needs & Solutions',
          'Identify Data Needs':                            'Identify Data Needs',
          'Manage AI Model Development and Evaluation':     'Model Development & Evaluation',
          'Operationalize AI Solution':                     'Operationalize AI Solution',
        },
      },
    },
    // ── Secondary dimension: PMI ECO exam domains ───────────────────────────
    // The six methodology phases (questions.category) stay the PRIMARY,
    // instructional taxonomy — "where am I weak in the AI project lifecycle?".
    // The ECO domain is the SECONDARY, exam-scoring taxonomy — "where am I weak
    // against PMI's Exam Content Outline?". They are independent; nothing may
    // sum, average or map one into the other. Declaring this block lights up
    // the existing config-driven secondary-dimension surfaces (dashboard card,
    // coach guidance, admin distribution) fed by learner_session_summary
    // .secondary_breakdown, which migration 095 scopes to eco_domain for CPMAI.
    // NOTE: deliberately NOT added to examCategories.SECONDARY_DIMENSION_VALUES
    // — that map drives the editor's optional focus_area field, and the editor
    // already has its own REQUIRED ECO Domain select (migration 096).
    secondaryDimension: {
      key:          'ecoDomain',
      column:       'eco_domain',
      metadataKey:  'ecoDomain',
      label:        'Exam-Domain Readiness',
      labelSingular:'PMI exam domain',
      values:       CPMAI_ECO_DOMAINS,
    },
    // ── Full Exam stratification: PMI ECO weights ───────────────────────────
    // PMI-CPMAI Exam Content Outline (September 2025). The real exam is scored
    // per domain at these weights, so the simulation draws by eco_domain.
    // categoryDistribution (below) is the METHODOLOGY mix and is retained for
    // reference/analytics; it no longer stratifies the Full Exam for CPMAI.
    // On a 120-question sim, largest-remainder rounding yields
    // 18 / 31 / 31 / 19 / 21.
    fullExamDistribution: {
      field: 'ecoDomain',
      percentages: {
        'Support Responsible and Trustworthy AI Efforts': 15,
        'Identify Business Needs and Solutions':          26,
        'Identify Data Needs':                            26,
        'Manage AI Model Development and Evaluation':     16,
        'Operationalize AI Solution':                     17,
      },
    },
    practiceExam: {
      defaultQuestions: 20,
      durationMinutes: 25,
      experimental: { enabled: false, count: 0 },
    },
    targetedQuiz: {
      defaultQuestions: 15,
      durationMinutes: 20,
      experimental: { enabled: false, count: 0 },
    },
    // Exam Ready threshold; CPMAI defaults to the same 75 as PMP. Tune later
    // if the cert's empirical pass band suggests a different target.
    pass_target_pct: 75,
    // CPMAI is a shorter prep cycle; same defaults as PMP for now.
    study_cadence: {
      days_on:     5,
      review_days: 1,
      rest_days:   1,
    },
    // Shorter prep cycle than PMP — a single full simulation is sufficient.
    target_full_sims: 1,
    categoryDistribution: {
      'Business Understanding':      41,
      'Data Understanding':          13,
      'Data Preparation':            13,
      'Model Development':            8,
      'Model Evaluation':             8,
      'Model Operationalization':    17,
    },
  },
  'pmi-acp': {
    examId: 'pmi-acp',
    displayName: 'PMI-ACP®',
    fullDescription: 'PMI Agile Certified Practitioner',
    // Marked Coming Soon until the question bank is built up.  The
    // ExamSelector renders a "Coming Soon" badge over the cert card and
    // disables the click for any exam with this flag set.  Existing
    // session flows are unaffected — a user who reaches them via deep
    // link or admin tooling still gets the full experience.
    comingSoon: false,
    fullExam: {
      totalQuestions: 120,
      durationMinutes: 180,
    },
    // PMI-ACP offers a single 10-minute break at the halfway point.
    // Same semantics as PMP — see the `breaks` comment on the pmp entry:
    // the position is DERIVED by getExamBreaks() (midpoint of
    // fullExam.totalQuestions), not asserted as a PMI-published question
    // number. ACP has no case section, so this is always the fallback path.
    breaks: [
      { durationMinutes: 10 },
    ],
    practiceExam: {
      defaultQuestions: 60,
      durationMinutes: 90,
    },
    targetedQuiz: {
      defaultQuestions: 15,
      durationMinutes: 20,
    },
    // 2026 PMI-ACP ECO published weights (sums to 100). Used by exam-build
    // logic that draws stratified samples from the `category` column.
    categoryDistribution: {
      'Mindset':    28,
      'Leadership': 25,
      'Product':    19,
      'Delivery':   28,
    },
  },
  // ── PMI-PMOCP™ ─────────────────────────────────────────────────────────────
  // PMI Project Management Office Certified Professional. Added as a
  // configuration-driven, first-class certification (Phase 1–4). Gated by the
  // `enablePmiPmocp` feature flag — when that flag is false getAllExams() omits
  // this entry entirely, so PMP / CPMAI / PMI-ACP behave exactly as before.
  //
  // Domains + weights are PMI's published PMI-PMOCP Exam Content Outline (2025).
  // Exam length (120 items / 165 min) is the published spec. These are real
  // PMI values, not placeholders — but every one is config here, never code,
  // so an admin can revise them without a code change.
  'pmi-pmocp': {
    examId: 'pmi-pmocp',
    displayName: 'PMI-PMOCP™',
    shortName: 'PMOCP',
    fullDescription: 'PMI Project Management Office Certified Professional',
    status: 'active',
    routeSlug: 'pmi-pmocp',
    // Feature-flag gate. When isFeatureEnabled('enablePmiPmocp') is false this
    // cert is filtered out of getAllExams()/getActiveCertIds() (hidden
    // everywhere). When true it is fully selectable.
    featureFlag: 'enablePmiPmocp',
    comingSoon: false,
    // The category vocabulary is "Domains" for PMO-CP, NOT PMP "Phases".
    metadataDisplay: {
      category: {
        groupLabel: 'Domains',
        valueAllowlist: [
          'Organizational Development and Alignment',
          'PMO Strategic Elements',
          'PMO Design and Structuring',
          'People',
          'PMO Operations and Performance',
          'PMO Enhancement and Effectiveness',
        ],
      },
      // Secondary dimension surfaced as a Targeted Test filter group + dashboard
      // analytics layer. Auto-discovered by extractMetadataOptions via the
      // matching METADATA_FIELDS entry (key 'focusArea').
      focusArea: {
        groupLabel: 'Focus Areas',
      },
    },
    // ── Secondary metadata dimension (config-driven) ─────────────────────────
    // A cert MAY declare ONE secondary dimension. When present, the app surfaces
    // it (targeted filtering, dashboard strengths/weaknesses, study-guide
    // guidance, admin distribution) WITHOUT any cert-specific conditionals.
    // Certs without this key behave exactly as before.
    //   key            app-side (camelCase) question field + metadata key
    //   column         questions table column (snake_case)
    //   metadataKey    Question Factory draft metadata key
    //   values         allowed vocabulary
    //   unclassified   fallback bucket for low-confidence/backfill
    secondaryDimension: {
      key:          'focusArea',
      column:       'focus_area',
      metadataKey:  'focusArea',
      label:        'Focus Areas',
      labelSingular:'Focus Area',
      values:       PMI_PMOCP_FOCUS_AREAS,
      unclassified: FOCUS_AREA_UNCLASSIFIED,
    },
    fullExam: {
      totalQuestions: 120,
      durationMinutes: 165, // PMI published: 2h45m
      experimental: { enabled: false, count: 0 },
    },
    // No scheduled breaks for now — update here (config only) if PMI's
    // published PMOCP exam experience adds one.
    breaks: [],
    practiceExam: {
      defaultQuestions: 20,
      durationMinutes: 25,
      experimental: { enabled: false, count: 0 },
    },
    targetedQuiz: {
      defaultQuestions: 15,
      durationMinutes: 20,
      experimental: { enabled: false, count: 0 },
    },
    pass_target_pct: 75,
    study_cadence: {
      days_on:     5,
      review_days: 1,
      rest_days:   1,
    },
    // New cert / small bank to start — one full simulation is the recommended
    // target until the bank is large enough to support more.
    target_full_sims: 1,
    // PMI-PMOCP Exam Content Outline (2025) domain weights. Sums to 100.
    categoryDistribution: {
      'Organizational Development and Alignment': 16,
      'PMO Strategic Elements':                   18,
      'PMO Design and Structuring':               18,
      'People':                                   15,
      'PMO Operations and Performance':           15,
      'PMO Enhancement and Effectiveness':        18,
    },
    // ── Extended certification-model fields ──────────────────────────────────
    // These richer descriptors make the cert fully self-describing for the
    // study plan, resource panel, Question Factory, and import flows without
    // any PMP-specific branching in code.
    domains: [
      'Organizational Development and Alignment',
      'PMO Strategic Elements',
      'PMO Design and Structuring',
      'People',
      'PMO Operations and Performance',
      'PMO Enhancement and Effectiveness',
    ],
    // Alias of categoryDistribution kept under the model's canonical name.
    domainWeights: {
      'Organizational Development and Alignment': 16,
      'PMO Strategic Elements':                   18,
      'PMO Design and Structuring':               18,
      'People':                                   15,
      'PMO Operations and Performance':           15,
      'PMO Enhancement and Effectiveness':        18,
    },
    // Study-plan macro phases (ratios of the runway). Shared shape across
    // certs; surfaced by studyPlanService / StudyPlan page.
    studyPlanPhases: [
      { id: 'foundation', label: 'Foundation', ratio: 0.5 },
      { id: 'practice',   label: 'Practice',   ratio: 0.3 },
      { id: 'polish',     label: 'Polish',     ratio: 0.2 },
    ],
    // Official PMI resources for the Resources panel.
    resourceLinks: [
      { label: 'Official PMI-PMOCP certification page',        url: 'https://www.pmi.org/certifications/pmo-certified-professional-pmi-pmocp' },
      { label: 'PMI-PMOCP Exam Content Outline (PDF)',         url: 'https://www.pmi.org/-/media/pmi/documents/public/pdf/certifications/pmi-pmocp-exam-content-outline_2025-1.pdf' },
      { label: 'Project Management Offices: A Practice Guide', url: 'https://www.pmi.org/standards/pmo' },
      // The old /certification/preparation deep link now redirects to the
      // PMOGA homepage; the prep-course page is the durable target.
      { label: 'PMI-PMOCP Prep Course (10-hour, PMOGA)',       url: 'https://pmoga.pmi.org/certification/pmi-pmocp-prep-course' },
      { label: 'PMO Value Ring methodology',                   url: 'https://pmovaluering.com/en/certifications' },
    ],
    // Identifier for the Question Factory prompt profile (see
    // questionFactory/promptProfiles).
    questionFactoryPromptProfile: 'pmi-pmocp',
    // Import-template descriptor consumed by BulkImport / importService.
    importTemplate: {
      referenceIdPrefix: 'PMOCP',
      categoryField: 'category',
      requiredColumns: ['question', 'choices', 'correctAnswers', 'category'],
    },
    dashboardLabels: {
      categoryLabel:        'Domains',
      categoryLabelSingular:'Domain',
    },
  },
};

// Default config for the user-configured Targeted Test mode. Shared by every
// exam unless an exam block overrides it. Kept top-level so a per-exam tweak
// is a one-line addition.
const DEFAULT_TARGETED_TEST = {
  allowedCounts:      [10, 15, 20, 25],
  defaultCount:       15,
  secondsPerQuestion: 90, // 1.5 min/question
};

// ── Intelligent Pace Coaching defaults ───────────────────────────────────────
// Single source of truth for pacing thresholds. Shared across certs; a cert
// may override any key via a `paceCoaching` block on its exam object. Consumed
// by getPaceConfig() below and passed straight into paceService.computePace().
// See docs/pace-coaching-proposal.md §8.
const DEFAULT_PACE_COACHING = {
  enabled: true,                                // master switch (per exam)
  appliesTo: ['full', 'practice', 'targetedTest'], // modes that may show pacing
  // PRIMARY — review-time confidence bands (minutes of projected review buffer)
  confidenceMinutes: { comfortable: 20, good: 10, tight: 5, atRisk: 0 },
  // Coarse pace status bands (seconds behind target) — gate messages only
  statusThresholdsSeconds: { ahead: 60, behind: 180, significantlyBehind: 600 },
  timeSinkSeconds: 180,          // single-question "time sink" nudge threshold
  recoveryMarginSeconds: 120,    // upward swing that qualifies as a recovery
  warmupQuestions: 20,           // below this, the live card stays neutral
  minSecondsBetweenMessages: 90, // frequency floor (all message types)
  liveTickSeconds: 8,            // live card refresh cadence
  showTimeline: false,           // live in-exam rail deferred (results-only v1)
};

/**
 * Derive exam-aware milestone positions (answered-question counts) for a cert.
 * Milestones are NOT a hardcoded interval — they follow the exam's own break
 * structure plus a "final stretch" checkpoint (totalQuestions - 30). Certs with
 * no scheduled breaks fall back to a midpoint. All values are clamped strictly
 * inside the exam. PMP -> [60, 120, 150]; PMI-ACP -> [60, 90]; CPMAI -> [60, 90].
 * @param {string} examId
 * @returns {number[]}
 */
function deriveMilestones(examId) {
  const cfg = exams[examId];
  const total = cfg && cfg.fullExam ? cfg.fullExam.totalQuestions : 0;
  if (!total) return [];
  const breakPts = getExamBreaks(examId).map((b) => b.afterQuestion);
  const base = breakPts.length ? breakPts : [Math.round(total / 2)];
  const finalStretch = total - 30;
  const all = [...base];
  if (finalStretch > 0 && !all.includes(finalStretch)) all.push(finalStretch);
  return [...new Set(all)].filter((n) => n > 0 && n < total).sort((a, b) => a - b);
}

/**
 * Returns the resolved pace-coaching config for an examId: shared defaults
 * merged with any per-cert `paceCoaching` override, plus exam-aware milestones
 * (explicit override wins; otherwise derived from breaks + final stretch).
 * Shape is compatible with paceService.computePace()'s `config` argument.
 * @param {string} examId
 * @returns {object|null}
 */
export function getPaceConfig(examId) {
  const cfg = exams[examId];
  if (!cfg) return null;
  const override = cfg.paceCoaching || {};
  return {
    ...DEFAULT_PACE_COACHING,
    ...override,
    confidenceMinutes: { ...DEFAULT_PACE_COACHING.confidenceMinutes, ...(override.confidenceMinutes || {}) },
    statusThresholdsSeconds: { ...DEFAULT_PACE_COACHING.statusThresholdsSeconds, ...(override.statusThresholdsSeconds || {}) },
    milestones: override.milestones || deriveMilestones(examId),
  };
}

/**
 * Returns the config object for a given examId, with `targetedTest` filled in
 * from the shared default when the exam block does not override it.
 * @param {string} examId - "pmp" | "cpmai" | "pmi-acp"
 * @returns {object|null}
 */
export function getExamConfig(examId) {
  const cfg = exams[examId];
  if (!cfg) return null;
  // Non-mutating merge so callers always see a `targetedTest` block.
  return {
    ...cfg,
    targetedTest: { ...DEFAULT_TARGETED_TEST, ...(cfg.targetedTest || {}) },
  };
}

/**
 * Returns all exam configs as an array (useful for rendering lists).
 * @returns {object[]}
 */

/**
 * Returns the NO-CASE / LEGACY FALLBACK break definitions for a cert's
 * Simulation / Full Exam, sorted ascending by afterQuestion. Returns an empty
 * array when the cert defines none (e.g. CPMAI) or the examId is unknown.
 * This is the ONLY source of break thresholds — nothing is hardcoded in
 * components.
 *
 * A break's `afterQuestion` is DERIVED (even fractions of
 * fullExam.totalQuestions) unless the cert config states one explicitly;
 * `derived: true` flags a computed position. PMI publishes the break COUNT and
 * DURATION but not question numbers, so a derived position is a PM Pixel
 * placement choice and must never be presented as a PMI-published value.
 *
 * When a case section can be assembled, the real placement comes from
 * services/examCaseBlocks.js computeSessionBreaks(), which overrides these.
 * @param {string} examId
 * @returns {{afterQuestion:number,durationMinutes:number}[]}
 */
export function getExamBreaks(examId) {
  const cfg = exams[examId];
  if (!cfg || !Array.isArray(cfg.breaks) || cfg.breaks.length === 0) return [];
  const total = (cfg.fullExam && cfg.fullExam.totalQuestions) || 0;
  const n = cfg.breaks.length;
  return cfg.breaks
    .map((b, i) => {
      const explicit = Number.isFinite(b.afterQuestion);
      return {
        // Even fractions of the exam: n breaks split it into n+1 stretches, so
        // break i lands at total * (i+1) / (n+1). For PMP (n=2, total=180) that
        // is Q60 and Q120 — the same numbers the legacy config asserted, but
        // now COMPUTED from the exam's own shape rather than copied from a
        // superseded PMI document. `derived: true` marks a fallback position
        // that must never be described as PMI-published.
        afterQuestion: explicit ? b.afterQuestion : Math.round((total * (i + 1)) / (n + 1)),
        durationMinutes: b.durationMinutes,
        derived: !explicit,
      };
    })
    .sort((a, b) => a.afterQuestion - b.afterQuestion);
}

/**
 * Returns the secondary-dimension config for an examId, or null if the cert does
 * not declare one. Drives focus-area targeting/analytics/study-guide/admin in a
 * config-driven way (no per-cert conditionals).
 * @param {string} examId
 * @returns {{key:string,column:string,metadataKey:string,label:string,labelSingular:string,values:string[],unclassified:string}|null}
 */
/**
 * Resolves the Full Exam stratification for a cert:
 *   { field, percentages } — `field` is the normalized question property to
 *   bucket on ('category' by default, e.g. 'ecoDomain' for CPMAI), and
 *   `percentages` sums to 100. Returns null when the cert declares neither
 *   fullExamDistribution nor categoryDistribution (plain shuffle-and-slice).
 * @param {string} examId
 * @returns {{field:string, percentages:Object<string,number>}|null}
 */
export function getFullExamDistribution(examId) {
  const cfg = exams[examId];
  if (!cfg) return null;
  if (cfg.fullExamDistribution && cfg.fullExamDistribution.percentages) {
    return {
      field: cfg.fullExamDistribution.field || 'category',
      percentages: cfg.fullExamDistribution.percentages,
    };
  }
  if (cfg.categoryDistribution) {
    return { field: 'category', percentages: cfg.categoryDistribution };
  }
  return null;
}

export function getSecondaryDimension(examId) {
  const cfg = exams[examId];
  return (cfg && cfg.secondaryDimension) ? cfg.secondaryDimension : null;
}

export function getAllExams() {
  // Hide any cert whose featureFlag resolves to false. Certs without a
  // featureFlag (PMP / CPMAI / PMI-ACP) are always returned, so existing
  // behavior is unchanged when no flags are off.
  return Object.values(exams).filter((e) => isFeatureEnabled(e.featureFlag));
}

/**
 * Returns the list of cert examIds that are NOT `comingSoon` (i.e., the
 * certifications that should appear in dashboards, milestones, readiness,
 * and study plan flows. Returns ['pmp', 'cpmai'] today; PMI-ACP joins the
 * moment `comingSoon` flips to false in this file.
 *
 * @returns {string[]}
 */
export function getActiveCertIds() {
  return getAllExams().filter((e) => !e.comingSoon).map((e) => e.examId);
}

/**
 * Returns the `learner_profiles` column name that stores the baseline date
 * for a given examId. PMP -> 'baseline_at_pmp', CPMAI -> 'baseline_at_cpmai',
 * PMI-ACP -> 'baseline_at_pmi_acp' (hyphen converted to underscore).
 *
 * @param {string} examId
 * @returns {string}
 */
export function getBaselineColumn(examId) {
  return 'baseline_at_' + (examId || '').replace(/-/g, '_');
}
